/**
 * Global Error Handler Plugin
 * Captures all unhandled errors (including from non-logged-in visitors)
 * and logs them to the system logs for admin visibility
 */
export default defineNuxtPlugin(nuxtApp => {
  // Only run on client-side
  if (!import.meta.client)
    return

  const { createLog } = useSupabaseLogs()

  /**
   * Log error to system with context
   */
  async function logClientError(
    category: 'page_error' | 'console_error' | 'network_error',
    message: string,
    error?: Error | unknown,
    details?: Record<string, unknown>,
  ) {
    try {
      const errorDetails: Record<string, unknown> = {
        ...details,
        url: window.location.href,
        referrer: document.referrer || null,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      }

      // Add error stack if available
      if (error instanceof Error) {
        errorDetails.errorName = error.name
        errorDetails.errorStack = error.stack
      }

      await createLog({
        level: 'error',
        category,
        message,
        details: errorDetails,
        error: error instanceof Error ? error : null,
      })
    }
    catch {
      // Silently fail to prevent infinite loops
      console.warn('[ErrorHandler] Failed to log error to system')
    }
  }

  // Capture Vue component errors
  nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
    // Always log to console first
    console.error('[Vue Error]', error)

    const componentName = instance?.$options?.name || instance?.$options?.__name || 'Unknown'
    const router = nuxtApp.$router as { currentRoute?: { value?: { fullPath?: string } } } | undefined

    logClientError('page_error', `Vue error in ${componentName}: ${info}`, error, {
      component: componentName,
      info,
      route: router?.currentRoute?.value?.fullPath,
    })
  }

  // Capture Vue warning in development (optional, can be noisy)
  if (process.dev) {
    nuxtApp.vueApp.config.warnHandler = (msg, instance, trace) => {
      console.warn('[Vue Warning]', msg, trace)

      // Not logging warnings to DB to avoid noise
    }
  }

  // Capture Nuxt/app-level errors
  nuxtApp.hook('vue:error', (error, instance, info) => {
    console.error('[Nuxt Error]', error)

    const router = nuxtApp.$router as { currentRoute?: { value?: { fullPath?: string } } } | undefined

    logClientError('page_error', `Nuxt error: ${info}`, error, {
      info,
      route: router?.currentRoute?.value?.fullPath,
    })
  })

  // Capture unhandled JavaScript errors (window.onerror)
  window.onerror = (message, source, lineno, colno, error) => {
    console.error('[Window Error]', message, source, lineno, colno)

    logClientError('console_error', String(message), error, {
      source,
      line: lineno,
      column: colno,
    })

    return false // Let error propagate
  }

  // Capture unhandled promise rejections
  window.onunhandledrejection = event => {
    console.error('[Unhandled Promise Rejection]', event.reason)

    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))

    logClientError('console_error', `Unhandled Promise Rejection: ${error.message}`, error, {
      type: 'unhandledrejection',
    })
  }

  // Capture network/fetch errors by patching fetch
  const originalFetch = window.fetch

  window.fetch = async function (...args) {
    const getUrl = (): string => {
      const input = args[0]
      if (typeof input === 'string')
        return input
      if (input instanceof URL)
        return input.href
      if (input instanceof Request)
        return input.url

      return 'unknown'
    }

    try {
      const response = await originalFetch.apply(this, args)

      // Log 5xx server errors (but not 4xx which are usually client errors)
      if (response.status >= 500) {
        const url = getUrl()

        logClientError('network_error', `Server error ${response.status} on ${url}`, undefined, {
          status: response.status,
          statusText: response.statusText,
          url,
          method: (args[1] as RequestInit)?.method || 'GET',
        })
      }

      return response
    }
    catch (error) {
      // Skip AbortError - this happens when user navigates away during a fetch
      // These are normal browser behavior, not actual errors
      if (error instanceof Error && error.name === 'AbortError') {
        throw error
      }

      // Skip "Failed to fetch" TypeError - usually happens during navigation
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw error
      }

      // Network failure (offline, CORS, etc.)
      const url = getUrl()

      logClientError('network_error', `Fetch failed for ${url}`, error, {
        url,
        method: (args[1] as RequestInit)?.method || 'GET',
      })

      throw error // Re-throw so the calling code can handle it
    }
  }
})
